import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Package, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [step, setStep] = useState("request"); // "request" → "confirm"
    const [email, setEmail] = useState("");
    const [token, setToken] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [busy, setBusy] = useState(false);

    // If we arrive from a reset-password email link (which carries ?token=...&email=...),
    // pre-fill the form and jump straight to the "set new password" step.
    useEffect(() => {
        const t = params.get("token");
        const e = params.get("email");
        if (t) {
            setToken(t);
            if (e) setEmail(e);
            setStep("confirm");
        }
    }, [params]);

    const requestReset = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { email });
            toast.success("Demande envoyée. Vérifie tes emails (ou les logs serveur).");
            setStep("confirm");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const confirmReset = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/reset-password/confirm", { token, new_password: newPassword });
            toast.success("Mot de passe réinitialisé ! Connectez-vous.");
            navigate("/login");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <Card className="w-full max-w-md border-border">
                <CardContent className="p-8 space-y-6">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                            <Package weight="duotone" size={20} />
                        </div>
                        <div className="font-display text-xl font-bold">ScanBox</div>
                    </div>

                    <div>
                        <p className="overline">Récupération</p>
                        <h2 className="font-display text-3xl font-bold mt-2 tracking-tight">
                            {step === "request" ? "Mot de passe oublié" : "Saisir le token reçu"}
                        </h2>
                        <p className="text-muted-foreground text-sm mt-2">
                            {step === "request"
                                ? "Saisis ton email — un lien/token de réinitialisation (valide 30 min) sera envoyé."
                                : "Colle le token reçu et choisis un nouveau mot de passe."}
                        </p>
                    </div>

                    <Alert className="bg-emerald-50/40 border-emerald-200">
                        <ShieldCheck size={16} className="text-emerald-600" />
                        <AlertDescription className="text-xs text-emerald-800 ml-2">
                            Réinitialisation sécurisée par token à usage unique (valide 30 min).
                        </AlertDescription>
                    </Alert>

                    {step === "request" ? (
                        <form className="space-y-4" onSubmit={requestReset}>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email du compte</Label>
                                <Input
                                    id="email" type="email" required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="vous@exemple.com"
                                    data-testid="forgot-email"
                                />
                            </div>
                            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90"
                                    data-testid="forgot-submit">
                                {busy ? "Envoi…" : "Demander la réinitialisation"}
                                <ArrowRight size={16} className="ml-2" />
                            </Button>
                            <button type="button"
                                    className="w-full text-xs text-muted-foreground hover:text-foreground underline"
                                    onClick={() => setStep("confirm")}>
                                J&apos;ai déjà un token
                            </button>
                        </form>
                    ) : (
                        <form className="space-y-4" onSubmit={confirmReset}>
                            <div className="space-y-2">
                                <Label htmlFor="token">Token de réinitialisation</Label>
                                <Input
                                    id="token" type="text" required
                                    value={token}
                                    onChange={(e) => setToken(e.target.value)}
                                    placeholder="Colle ici le token reçu"
                                    data-testid="forgot-token"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="newpass">Nouveau mot de passe (6 caractères min.)</Label>
                                <Input
                                    id="newpass" type="password" required minLength={6}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    data-testid="forgot-password"
                                />
                            </div>
                            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90"
                                    data-testid="forgot-confirm">
                                {busy ? "Validation…" : "Valider le nouveau mot de passe"}
                                <ArrowRight size={16} className="ml-2" />
                            </Button>
                            <button type="button"
                                    className="w-full text-xs text-muted-foreground hover:text-foreground underline"
                                    onClick={() => setStep("request")}>
                                Renvoyer une demande
                            </button>
                        </form>
                    )}

                    <div className="text-sm text-muted-foreground text-center">
                        <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="link-back-login">
                            Retour à la connexion
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
